/** @jsx jsx */
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { jsx } from '@emotion/core';
import rafSchedule from 'raf-schd';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import useLazyCallback from '@atlaskit/ds-lib/use-lazy-callback';
import useStateRef from '@atlaskit/ds-lib/use-state-ref';
import { keylineHeight } from '../constants';
import { scrollableStyles } from '../styles/scroll-container';

/**
 * A container that shows top and bottom keylines when the
 * content overflows into the scrollable element.
 */
const ScrollContainer = /*#__PURE__*/forwardRef((props, ref) => {
  const [hasSiblings, setSiblings] = useStateRef({
    previous: false,
    next: false
  });
  const [showContentFocus, setContentFocus] = useState(false);
  const [showTopKeyline, setTopKeyline] = useState(false);
  const [showBottomKeyline, setBottomKeyline] = useState(false);
  const scrollableRef = useRef(null);
  const setLazySiblings = useLazyCallback(setSiblings);
  const setLazyContentFocus = useLazyCallback(rafSchedule(() => {
    const target = scrollableRef.current;
    target && setContentFocus(target.scrollHeight > target.clientHeight);
  }));
  const setLazyKeylines = useLazyCallback(rafSchedule(() => {
    const target = scrollableRef.current;

    if (target) {
      const scrollableDistance = target.scrollHeight - target.clientHeight;

      if (hasSiblings.current.previous) {
        setTopKeyline(target.scrollTop > keylineHeight);
      }

      if (hasSiblings.current.next) {
        setBottomKeyline(target.scrollTop <= scrollableDistance - keylineHeight);
      }
    }
  }));
  useEffect(() => {
    const target = scrollableRef.current;
    window.addEventListener('resize', setLazyKeylines, false);
    target === null || target === void 0 ? void 0 : target.addEventListener('scroll', setLazyKeylines, false);
    setLazyContentFocus();
    setLazyKeylines();
    setLazySiblings({
      previous: Boolean(target === null || target === void 0 ? void 0 : target.previousElementSibling),
      next: Boolean(target === null || target === void 0 ? void 0 : target.nextElementSibling)
    });
    return () => {
      window.removeEventListener('resize', setLazyKeylines, false);
      target === null || target === void 0 ? void 0 : target.removeEventListener('scroll', setLazyKeylines, false);
    };
  }, [setLazyContentFocus, setLazyKeylines, setLazySiblings]);
  return jsx("div", {
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    tabIndex: showContentFocus ? 0 : undefined,
    "data-testid": props.testId && `${props.testId}--scrollable`,
    ref: mergeRefs([ref, scrollableRef]),
    css: scrollableStyles({
      showTopKeyline,
      showBottomKeyline,
      shouldScroll: props.shouldScroll
    })
  }, props.children);
});
ScrollContainer.displayName = 'ScrollContainer';
export default ScrollContainer;